import React, { useState, useEffect } from 'react';
import Header from '../components/Layout/Header';
import { attendanceAPI, employeeAPI, departmentAPI, shiftAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Clock, LogIn, LogOut, Calendar, Users, AlertCircle, CheckCircle, UserPlus, Edit, MapPin } from 'lucide-react';

const Attendance = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'hr', 'verifier'].includes(user?.role);
  const isVerifier = user?.role === 'verifier';
  
  // Regular attendance state
  const [todayStatus, setTodayStatus] = useState(null);
  const [myAttendance, setMyAttendance] = useState([]);
  const [allAttendance, setAllAttendance] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedDept, setSelectedDept] = useState('');
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  
  // Regularization modal
  const [showRegularize, setShowRegularize] = useState(false);
  const [regularizeData, setRegularizeData] = useState({
    employee_id: '',
    date: '',
    check_in_time: '09:00:00',
    check_out_time: '18:00:00',
    reason: ''
  });
  
  // Check-in for other employees modal (Admin/Verifier)
  const [showCheckInOther, setShowCheckInOther] = useState(false);
  const [checkInOtherData, setCheckInOtherData] = useState({
    employee_id: '',
    action: 'check_in',
    time: ''
  });
  
  // Update attendance modal (Admin)
  const [showUpdateAttendance, setShowUpdateAttendance] = useState(false);
  const [updateAttendanceData, setUpdateAttendanceData] = useState({
    attendance_id: '',
    employee_id: '',
    employee_name: '',
    date: '',
    check_in_time: '',
    check_out_time: '',
    status: 'present'
  });

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedDept]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [todayRes, myAttRes] = await Promise.all([
        attendanceAPI.getToday(),
        attendanceAPI.getMyAttendance(selectedMonth)
      ]);
      setTodayStatus(todayRes.data);
      setMyAttendance(myAttRes.data);

      if (isAdmin) {
        const [allRes, deptRes, empRes, shiftRes] = await Promise.all([
          attendanceAPI.getAll({ month: selectedMonth, department_id: selectedDept || undefined }),
          departmentAPI.getAll(),
          employeeAPI.getAll(),
          shiftAPI.getAll()
        ]);
        setAllAttendance(allRes.data);
        setDepartments(deptRes.data);
        setEmployees(empRes.data);
        setShifts(shiftRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch attendance data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);
    try {
      let location = null;
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          location = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        } catch (e) {
          // console.log('Location not available');
        }
      }
      
      await attendanceAPI.checkIn({ location });
      toast.success('Checked in successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleCheckOut = async () => {
    setCheckingIn(true);
    try {
      let location = null;
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          location = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        } catch (e) {
          // console.log('Location not available');
        }
      }
      
      await attendanceAPI.checkOut({ location });
      toast.success('Checked out successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-out failed');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleRegularize = async () => {
    try {
      await attendanceAPI.regularize(regularizeData.employee_id, {
        date: regularizeData.date,
        check_in_time: regularizeData.check_in_time,
        check_out_time: regularizeData.check_out_time,
        reason: regularizeData.reason
      });
      toast.success('Attendance regularized successfully');
      setShowRegularize(false);
      setRegularizeData({ employee_id: '', date: '', check_in_time: '09:00:00', check_out_time: '18:00:00', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regularize attendance');
    }
  };

  const handleCheckInOther = async () => {
    if (!checkInOtherData.employee_id) {
      toast.error('Please select an employee');
      return;
    }
    
    setCheckingIn(true);
    try {
      const currentTime = checkInOtherData.time || new Date().toTimeString().slice(0, 8);
      
      if (checkInOtherData.action === 'check_in') {
        await attendanceAPI.checkInForEmployee(checkInOtherData.employee_id, { check_in_time: currentTime });
        toast.success('Employee checked in successfully');
      } else {
        await attendanceAPI.checkOutForEmployee(checkInOtherData.employee_id, { check_out_time: currentTime });
        toast.success('Employee checked out successfully');
      }
      
      setShowCheckInOther(false);
      setCheckInOtherData({ employee_id: '', action: 'check_in', time: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleUpdateAttendance = async () => {
    if (!updateAttendanceData.attendance_id) {
      toast.error('No attendance record selected');
      return;
    }
    
    try {
      await attendanceAPI.updateAttendance(updateAttendanceData.attendance_id, {
        check_in_time: updateAttendanceData.check_in_time,
        check_out_time: updateAttendanceData.check_out_time,
        status: updateAttendanceData.status
      });
      toast.success('Attendance updated successfully');
      setShowUpdateAttendance(false);
      setUpdateAttendanceData({
        attendance_id: '',
        employee_id: '',
        employee_name: '',
        date: '',
        check_in_time: '',
        check_out_time: '',
        status: 'present'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update attendance');
    }
  };

  const openUpdateModal = (record) => {
    setUpdateAttendanceData({
      attendance_id: record.id,
      employee_id: record.employee_id,
      employee_name: record.employee_name,
      date: record.date,
      check_in_time: record.check_in_time || '',
      check_out_time: record.check_out_time || '',
      status: record.status || 'present'
    });
    setShowUpdateAttendance(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      present: 'bg-emerald-100 text-emerald-700',
      absent: 'bg-red-100 text-red-700',
      half_day: 'bg-amber-100 text-amber-700',
      on_leave: 'bg-blue-100 text-blue-700',
      wfh: 'bg-purple-100 text-purple-700',
      holiday: 'bg-gray-100 text-gray-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div data-testid="attendance-page">
      <Header 
        title="Attendance Management" 
        subtitle="Track and manage attendance records"
      />
      
      <div className="p-8">
        {/* Today's Status Card - Hide for verifiers */}
        {!isVerifier && (
        <div className="dashboard-card mb-8" data-testid="today-status-card">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">Today&apos;s Attendance</h2>
              <p className="text-slate-500 text-sm">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
            <div className="flex items-center gap-4">
              {todayStatus?.checked_in && (
                <div className="text-right mr-4">
                  <p className="text-sm text-slate-500">Check-in</p>
                  <p className="font-medium text-emerald-600">{todayStatus.check_in_time}</p>
                </div>
              )}
              {todayStatus?.checked_out && (
                <div className="text-right mr-4">
                  <p className="text-sm text-slate-500">Check-out</p>
                  <p className="font-medium text-red-500">{todayStatus.check_out_time}</p>
                </div>
              )}
              {todayStatus?.working_hours > 0 && (
                <div className="text-right mr-4">
                  <p className="text-sm text-slate-500">Hours</p>
                  <p className="font-medium text-slate-700">{todayStatus.working_hours}h</p>
                </div>
              )}
              
              {!todayStatus?.checked_in ? (
                <Button 
                  onClick={handleCheckIn} 
                  disabled={checkingIn}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="check-in-btn"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Check In
                </Button>
              ) : !todayStatus?.checked_out ? (
                <Button 
                  onClick={handleCheckOut} 
                  disabled={checkingIn}
                  className="bg-red-500 hover:bg-red-600"
                  data-testid="check-out-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Check Out
                </Button>
              ) : (
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Day Complete</span>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <Input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-40"
              data-testid="month-filter"
            />
          </div>
          {isAdmin && (
            <>
              <Select value={selectedDept} onValueChange={setSelectedDept}>
                <SelectTrigger className="w-48" data-testid="department-filter">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                onClick={() => setShowCheckInOther(true)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="check-in-other-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Check-in for Employee
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowRegularize(true)}
                data-testid="regularize-btn"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                Regularize Attendance
              </Button>
            </>
          )}
        </div>

        {/* My Attendance History */}
        {!isAdmin && (
          <div className="dashboard-card mb-8" data-testid="my-attendance-section">
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">My Attendance History</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Check In</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Check Out</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Working Hours</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Overtime</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {myAttendance.map((record) => (
                    <tr key={record.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium">{record.date}</td>
                      <td className="py-3 px-4 text-emerald-600">{record.check_in_time || '-'}</td>
                      <td className="py-3 px-4 text-red-500">{record.check_out_time || '-'}</td>
                      <td className="py-3 px-4">{record.working_hours || 0}h</td>
                      <td className="py-3 px-4 text-amber-600">{record.overtime_hours || 0}h</td>
                      <td className="py-3 px-4">{getStatusBadge(record.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {myAttendance.length === 0 && (
                <p className="text-center py-8 text-slate-400">No attendance records found</p>
              )}
            </div>
          </div>
        )}

        {/* All Attendance (Admin View) */}
        {isAdmin && (
          <div className="dashboard-card" data-testid="all-attendance-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold text-slate-900">All Attendance Records</h2>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Users className="w-4 h-4" />
                <span>{allAttendance.length} records</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Employee</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Check In</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Check Out</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Location</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Hours</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">OT</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Regularized</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allAttendance.map((record) => (
                    <tr key={record.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium">{record.employee_name}</p>
                          <p className="text-xs text-slate-400">{record.employee_code}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">{record.date}</td>
                      <td className="py-3 px-4 text-emerald-600">{record.check_in_time || '-'}</td>
                      <td className="py-3 px-4 text-red-500">{record.check_out_time || '-'}</td>
                      <td className="py-3 px-4">
                        {record.check_in_coordinates?.latitude ? (
                          <a 
                            href={`https://maps.google.com/?q=${record.check_in_coordinates.latitude},${record.check_in_coordinates.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                            title={record.check_in_coordinates.address || 'View on map'}
                          >
                            <MapPin className="w-3 h-3" />
                            {record.check_in_coordinates.latitude.toFixed(4)}, {record.check_in_coordinates.longitude.toFixed(4)}
                          </a>
                        ) : record.check_in_location?.lat ? (
                          <a 
                            href={`https://maps.google.com/?q=${record.check_in_location.lat},${record.check_in_location.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <MapPin className="w-3 h-3" />
                            {record.check_in_location.lat.toFixed(4)}, {record.check_in_location.lng.toFixed(4)}
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">{record.working_hours || 0}h</td>
                      <td className="py-3 px-4 text-amber-600">{record.overtime_hours || 0}h</td>
                      <td className="py-3 px-4">{getStatusBadge(record.status)}</td>
                      <td className="py-3 px-4">
                        {record.is_regularized && (
                          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">Yes</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openUpdateModal(record)}
                          data-testid={`edit-attendance-${record.id}`}
                          title="Update Attendance"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {allAttendance.length === 0 && (
                <p className="text-center py-8 text-slate-400">No attendance records found</p>
              )}
            </div>
          </div>
        )}

        {/* Regularization Modal */}
        <Dialog open={showRegularize} onOpenChange={setShowRegularize}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Regularize Attendance</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Employee</Label>
                <Select 
                  value={regularizeData.employee_id} 
                  onValueChange={(v) => setRegularizeData({...regularizeData, employee_id: v})}
                >
                  <SelectTrigger data-testid="regularize-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.employee_code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={regularizeData.date}
                  onChange={(e) => setRegularizeData({...regularizeData, date: e.target.value})}
                  data-testid="regularize-date"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Check In Time</Label>
                  <Input
                    type="time"
                    value={regularizeData.check_in_time?.slice(0, 5)}
                    onChange={(e) => setRegularizeData({...regularizeData, check_in_time: e.target.value + ':00'})}
                    data-testid="regularize-check-in"
                  />
                </div>
                <div>
                  <Label>Check Out Time</Label>
                  <Input
                    type="time"
                    value={regularizeData.check_out_time?.slice(0, 5)}
                    onChange={(e) => setRegularizeData({...regularizeData, check_out_time: e.target.value + ':00'})}
                    data-testid="regularize-check-out"
                  />
                </div>
              </div>
              <div>
                <Label>Reason</Label>
                <Input
                  value={regularizeData.reason}
                  onChange={(e) => setRegularizeData({...regularizeData, reason: e.target.value})}
                  placeholder="Enter reason for regularization"
                  data-testid="regularize-reason"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRegularize(false)}>Cancel</Button>
              <Button onClick={handleRegularize} data-testid="regularize-submit-btn">Regularize</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Check-in for Other Employees Modal */}
        <Dialog open={showCheckInOther} onOpenChange={setShowCheckInOther}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Check-in/out for Employee</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Employee</Label>
                <Select 
                  value={checkInOtherData.employee_id} 
                  onValueChange={(v) => setCheckInOtherData({...checkInOtherData, employee_id: v})}
                >
                  <SelectTrigger data-testid="checkin-other-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.employee_code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Action</Label>
                <Select 
                  value={checkInOtherData.action} 
                  onValueChange={(v) => setCheckInOtherData({...checkInOtherData, action: v})}
                >
                  <SelectTrigger data-testid="checkin-other-action-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="check_in">Check In</SelectItem>
                    <SelectItem value="check_out">Check Out</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Time (optional - leave blank for current time)</Label>
                <Input
                  type="time"
                  value={checkInOtherData.time?.slice(0, 5) || ''}
                  onChange={(e) => setCheckInOtherData({...checkInOtherData, time: e.target.value ? e.target.value + ':00' : ''})}
                  data-testid="checkin-other-time"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCheckInOther(false)}>Cancel</Button>
              <Button 
                onClick={handleCheckInOther} 
                disabled={checkingIn}
                className={checkInOtherData.action === 'check_in' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-500 hover:bg-red-600'}
                data-testid="checkin-other-submit-btn"
              >
                {checkingIn ? 'Processing...' : (checkInOtherData.action === 'check_in' ? 'Check In' : 'Check Out')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Update Attendance Modal */}
        <Dialog open={showUpdateAttendance} onOpenChange={setShowUpdateAttendance}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Attendance</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{updateAttendanceData.employee_name}</p>
                <p className="text-sm text-slate-500">Date: {updateAttendanceData.date}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Check In Time</Label>
                  <Input
                    type="time"
                    value={updateAttendanceData.check_in_time?.slice(0, 5) || ''}
                    onChange={(e) => setUpdateAttendanceData({...updateAttendanceData, check_in_time: e.target.value ? e.target.value + ':00' : ''})}
                    data-testid="update-attendance-check-in"
                  />
                </div>
                <div>
                  <Label>Check Out Time</Label>
                  <Input
                    type="time"
                    value={updateAttendanceData.check_out_time?.slice(0, 5) || ''}
                    onChange={(e) => setUpdateAttendanceData({...updateAttendanceData, check_out_time: e.target.value ? e.target.value + ':00' : ''})}
                    data-testid="update-attendance-check-out"
                  />
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <Select 
                  value={updateAttendanceData.status} 
                  onValueChange={(v) => setUpdateAttendanceData({...updateAttendanceData, status: v})}
                >
                  <SelectTrigger data-testid="update-attendance-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                    <SelectItem value="half_day">Half Day</SelectItem>
                    <SelectItem value="wfh">Work From Home</SelectItem>
                    <SelectItem value="on_leave">On Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUpdateAttendance(false)}>Cancel</Button>
              <Button onClick={handleUpdateAttendance} data-testid="update-attendance-submit-btn">Update</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Attendance;
